// src/pos-app/shell/useBusinessDayGate.js
//
// F12a business-day-guard redirect-loop fix. Two station shells (KDS, Waiter)
// host pages that carry useBusinessDayGuard (src/hooks/pos/useBusinessDayGuard.js:
// OrderViewPage, TableServicePage) — that hook navigate('/functions',
// {replace:true}) whenever there is no active business day. The stations
// blind-revert ANY pathname departure back to the wrapped page (see each
// station's escape-interception effect), so the guard's own navigate got
// reverted straight back onto the page that re-fires it — an infinite
// redirect loop. isBusinessDay defaults to false (tenantStorage.js), so this
// is the NORMAL condition outside opening hours, not an edge case.
//
// The fix is upstream of the loop: read the SAME state source the guard
// reads (tenantStorage.getBusinessDayState(), same truthiness — plain
// `isBusinessDay` boolean, already normalized by tenantStorage) and let the
// station shell decide whether to mount the wrapped page at all. If it's
// never mounted, the guard never fires, so there is nothing to revert.
//
// tenantStorage has no change-event emission (grepped — none), so this polls.
// The interval is deliberately generous (a business day opening mid-poll is
// noticed within POLL_MS at worst) plus two cheap freshness triggers: window
// focus (device woken/app foregrounded) and an exposed manual refresh() for
// a user-facing retry affordance.
import { useCallback, useEffect, useRef, useState } from 'react';
import tenantStorage from '../../services/tenantStorage';

const POLL_MS = 45000;

export default function useBusinessDayGate() {
    const [dayOpen, setDayOpen] = useState(false);
    const [loading, setLoading] = useState(true);
    const activeRef = useRef(true);

    const refresh = useCallback(async () => {
        setLoading(true);
        try {
            const state = await tenantStorage.getBusinessDayState();
            // Exact match to useBusinessDayGuard's `!state?.isBusinessDay` check —
            // tenantStorage.getBusinessDayState() already normalizes isBusinessDay
            // to a real boolean, so `!!` here is belt-and-braces, not a semantics change.
            if (activeRef.current) setDayOpen(!!state?.isBusinessDay);
        } catch (e) {
            console.error('Business day gate check failed:', e);
            if (activeRef.current) setDayOpen(false);
        } finally {
            if (activeRef.current) setLoading(false);
        }
    }, []);

    useEffect(() => {
        activeRef.current = true;
        refresh();

        const interval = setInterval(refresh, POLL_MS);
        const onFocus = () => refresh();
        window.addEventListener('focus', onFocus);

        return () => {
            activeRef.current = false;
            clearInterval(interval);
            window.removeEventListener('focus', onFocus);
        };
    }, [refresh]);

    return { dayOpen, loading, refresh };
}
